/**
 * **Balance the load** — lend part of a recruiter's week to another cluster, and commit that
 * arrangement until the next cluster-lead weekly.
 *
 * ⚠️ IT LIVES ON THE CLUSTER VIEW NOW (Theresa, Sep 25 2026). It used to sit on the score tab,
 * which is the page the rest of the company reads: a control for re-cutting TA's own week was the
 * third thing a hiring manager saw, and the leads who actually use it were one tab away from the
 * cluster figures they use it against. This is the weekly's own view, so the lever belongs here.
 *
 * ⚠️ IT HOLDS NO STATE OF ITS OWN. Everything comes from the cockpit's one prioritisation state
 * (`RolePrioritisationProvider`), which is the same INSTANCE the Company View prices its clusters
 * from — so a lend made here moves those figures on the next render rather than on the next
 * remount. `userEmail` is still a prop because a commitment records who made it.
 *
 * ⚠️ STILL BROWSER-LOCAL, which is most of the point missing and is the next piece of work: a
 * commitment the other cluster leads cannot see is an arrangement only its author knows about. The
 * allocations move server-side in their own change (open decision 3 in CLAUDE.md); nothing else in
 * this component changes when they do, because the hook is what would gain the write.
 */
import { SEED_POOL } from '../../../types/rolePrioritisation';
import { SupportBoard } from './SupportBoard';
import { useRolePrioritisationState } from './RolePrioritisationProvider';

export function BalanceTheLoad({ userEmail }: { userEmail?: string | null }) {
  const {
    state,
    clusterKeys,
    clusterStatsList,
    model,
    cap,
    weekly,
    expired,
    warnings,
    suggest,
    applyState,
    applyEdit,
  } = useRolePrioritisationState();

  return (
    <SupportBoard
      stats={clusterStatsList}
      state={state}
      model={model}
      cap={cap}
      weeklyLabel={weekly.label}
      expired={expired}
      onStateChange={applyState}
      onSuggest={suggest}
      onCommit={() =>
        applyEdit((s) => {
          // Stamp committedAt on any allocation that is still draft, so those rows carry over as
          // the next weekly's default even after Revise (which clears the whole-plan lock). Rows
          // already carried over from a previous weekly keep their earlier committedAt.
          const nowIso = new Date().toISOString();
          return {
            ...s,
            allocations: s.allocations.map((a) => (a.committedAt ? a : { ...a, committedAt: nowIso })),
            commitment: {
              by: userEmail || 'TA lead',
              at: new Date().toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              }),
              validUntil: weekly.label,
              validUntilIso: weekly.iso,
            },
          };
        }, `Committed the support plan until ${weekly.label}`)
      }
      onRevise={() =>
        applyEdit((s) => ({ ...s, commitment: null }), 'Reopened the committed support plan')
      }
      onClear={() =>
        applyEdit(
          (s) => ({
            ...s,
            allocations: [],
            commitment: null,
            poolOut: SEED_POOL.out,
            interns: Object.fromEntries(clusterKeys.map((k) => [k, 0])),
            out: Object.fromEntries(clusterKeys.map((k) => [k, 0])),
          }),
          'Cleared every support allocation and capacity lever'
        )
      }
      warnings={warnings}
    />
  );
}
